import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Auth endpoints whose own 401s must NOT trigger a global redirect:
 *  - `/auth/login`            → wrong credentials, handled on the login page itself
 *  - `/auth/me`               → the bootstrap session probe, handled by restoreSession()
 *  - `/auth/logout`           → already logging out
 *  - `/auth/change-password`  → wrong current password, handled on the profile page itself
 */
const AUTH_PATHS = ['/auth/login', '/auth/me', '/auth/logout', '/auth/change-password'];

/**
 * When an authenticated request comes back `401` (expired/invalid session), clear
 * the local principal and route to `/login` once — a clean logout instead of the
 * app silently erroring until the next page reload. Auth-flow endpoints are
 * skipped so they can surface their own errors, and we only act while a session
 * is still considered active to avoid redundant navigations.
 */
export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((err: unknown) => {
      const isAuthPath = AUTH_PATHS.some((path) => req.url.includes(path));
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        !isAuthPath &&
        auth.isLoggedIn()
      ) {
        auth.clearSession();
        void router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
};
